import _StackFrame from 'stackframe';
import ErrorStackParser from 'error-stack-parser';
import StacktraceGPS from 'stacktrace-gps';

export class StackFrame extends _StackFrame {
	constructor(options: _StackFrame.StackFrameOptions) {
		super(options);
	};
};
const gps = new StacktraceGPS;

export class StackFrames {
	static async fromError(error: Error) {
		const frames = await Promise.all(
			ErrorStackParser.parse(error).
			map(async (frame) => {
				try {
					return (await gps.pinpoint(frame));
				} catch (e) {
				}
				return (frame);
			})
		);
		const sf = new StackFrames;
		sf.frames.push(...frames);
		return (sf);
	};

	static async fromStack(stack: string) {
		return (await this.fromError(<Error>{
			stack
		}));
	};

	static async fromPosition() {
		const stackFrames = await this.fromError(new Error);
		stackFrames.frames = stackFrames.frames.slice(2);
		return (stackFrames);
	};

	frames: StackFrame[] = [];
};

export interface IErrorAttributes {
	readonly isExtendedError: true;
	readonly isUserError: boolean;
	readonly isLibraryError: boolean;
};

export interface IErrorMetadata<TDetails extends object = Record<string, unknown>> {
	readonly boundError?: TErrorExtended | Error; // Error who caused this error
	readonly boundEvent?: Event; // Event who caused this error
	readonly bound?: unknown; // Something undetermined who caused this error
	readonly details: TDetails;
};

export type TErrorExtended<TDetails extends object = Record<string, unknown>> =
	Error
	& IErrorAttributes
	& IErrorMetadata<TDetails>;

const all: TErrorExtended[] = [];
let last: TErrorExtended | null = null;
const createError = <
	TDetails extends object,
	TIsUserError extends boolean,
	TIsLibraryError extends boolean
>(
	Error: ErrorConstructor,
	msg: string,
	data0: IErrorMetadata<TDetails>,
	data1: {
		isUserError: TIsUserError;
		isLibraryError: TIsLibraryError;
	}
) => {
	const error = Object.assign(new Error(msg), {
		...data0,
		...data1,
		isExtendedError: true
	} as const) satisfies TErrorExtended<TDetails>;
	addError(<TErrorExtended>error);
	return (error);
};
const createInfo = async (error: Error | TErrorExtended) => {
	return Object.assign(
		{
			name: error.name,
			message: error.message,
			stacktrace: (await StackFrames.fromError(error)).frames
		},
		!isExtendedError(error) ? {
			type: 'native'
		} as const : {
			type: error.isLibraryError ? 'library' : 'user',
			meta: {
				boundError: error.boundError,
				boundEvent: error.boundEvent,
				bound: error.bound,
				details: error.details
			}
		} as const
	) satisfies {
		name: string;
		message: string;
		stacktrace: StackFrames['frames'];
	} & (
		{
			type: 'native';
		} |
		{
			type: 'library' | 'user';
			meta: IErrorMetadata;
		}
		);
};

const defaultErrorHandler = async (error: TErrorExtended) => {
	console.error(await summarize(error));
};
let errorHandler = defaultErrorHandler;

export function attachErrorHandler(callback: (error: TErrorExtended) => Promise<void>) {
	errorHandler = callback;
}

export function detachErrorHandler() {
	errorHandler = defaultErrorHandler;
}

const onWindowErrorEvent = async (event: ErrorEvent) => {
	event.preventDefault();
	const error = addError(bindAsLibrary('Unhandled error!', {}, 'error' in event ? event.error : event));
	event.stopImmediatePropagation();
	await errorHandler(error);
	return true;
};
const onWindowPromiseErrorEvent = async (event: PromiseRejectionEvent) => {
	event.preventDefault(); // Not working in Firefox 😓
	const error = addError(bindAsLibrary('Unhandled promise error!', {}, event.reason));
	event.stopImmediatePropagation();
	await errorHandler(error);
	return true;
};

if (globalThis.window) { // make it compatible with Node.js
	window.addEventListener('error', onWindowErrorEvent);
	window.addEventListener('unhandledrejection', onWindowPromiseErrorEvent);
}

export function user<TDetails extends object = Record<string, unknown>>(
	msg: string,
	details: TDetails
) {
	return createError<TDetails, true, false>(
		Error,
		msg,
		{
			details
		}, {
			isUserError: true,
			isLibraryError: false
		}
	);
};

export function library<TDetails extends object = Record<string, unknown>>(
	msg: string,
	details: TDetails
) {
	return createError<TDetails, false, true>(
		Error,
		msg,
		{
			details
		}, {
			isUserError: false,
			isLibraryError: true
		}
	);
};

function bind<TDetails extends object = Record<string, unknown>>(
	msg: string,
	details: TDetails,
	boundTo: Error | Event | object,
	type: 'user' | 'library'
) {
	const boundErrorIsAnError = boundTo instanceof Error;
	const boundErrorIsAnEvent = boundTo instanceof Event;

	const error = (type === 'user' ? user : library)(msg, details);
	if (boundErrorIsAnError) {
		(<O.SetWritable<typeof error, 'boundError'>>error).boundError = boundTo;
	} else if (boundErrorIsAnEvent) {
		(<O.SetWritable<typeof error, 'boundEvent'>>error).boundEvent = boundTo;
	} else {
		(<O.SetWritable<typeof error, 'bound'>>error).bound = boundTo;
	}
	/* List only topmost error so remove sub error. */
	{
		const index = all.indexOf(<TErrorExtended>boundTo);
		if (index !== -1) {
			all.splice(index, 1);
		}
	}
	return (error);
};


export function bindAsUser<TDetails extends object = Record<string, unknown>>(
	msg: string,
	details: TDetails,
	boundTo: Error | Event | object
) {
	return bind(msg, details, boundTo, 'user');
};

export function bindAsLibrary<TDetails extends object = Record<string, unknown>>(
	msg: string,
	details: TDetails,
	boundTo: Error | Event | object
) {
	return bind(msg, details, boundTo, 'library');
};

/**
 * Apply the same type('user' or 'library') that boundTo if it is an TExtendedError or use library if not.
 **/
export function bindAsBound<TDetails extends object = Record<string, unknown>>(
	message: string,
	details: TDetails,
	boundTo: Error | Event | object
) {
	if (isExtendedError(boundTo)) {
		return (boundTo.isUserError ? bindAsUser : bindAsLibrary)(message, details, boundTo);
	} else {
		return bind(message, details, boundTo, 'library');
	}
};


export function clear() {
	all.length = 0;
	last = null;
};

export async function display(error: TErrorExtended) {
	const infos = (
		(await summarize(error)).map(x => `  ${x.type}: ${x.name}: ${x.message}`)
	);
	console.error(infos.join('\n'));
	console.error(error.stack);
};

export function isExtendedError(error: any): error is TErrorExtended {
	return error && typeof error === 'object' && 'details' in error && 'isExtendedError' in error;
};

export function handle<T extends void | Promise<void>>(
	error: unknown | null,
	_switch: {
		success?: () => T;
		userError?: (error: TErrorExtended) => T;
		libraryError?: (error: TErrorExtended) => T;
		unknownError?: (error: unknown) => T;
		unhandledError?: (error: unknown) => T;
	}
): T | null {
	if (!error) {
		if (_switch.success) {
			return _switch.success();
		}
	} else {
		if (isExtendedError(error)) {
			if (error.isUserError) {
				if (_switch.userError) {
					return _switch.userError(error);
				}
			} else if (error.isLibraryError) {
				if (_switch.libraryError) {
					return _switch.libraryError(error);
				}
			}
		} else if (_switch.unknownError) {
			return _switch.unknownError(error);
		}
		if (_switch.unhandledError) {
			return _switch.unhandledError(error);
		}
	}
	return null;
};

export async function summarize(error: TErrorExtended) {
	const out = [];
	let currentError: Error | TErrorExtended | null = error;
	while (currentError) {
		out.push(await createInfo(currentError));
		currentError = isExtendedError(currentError) ? currentError.boundError || null : null;
	}
	if (out.length === 1) {
		return (out);
	}
	return (out.reverse());
};

export async function summarizeLastError() {
	const error = getLast();
	if (!error) {
		throw user('There no error to summarize!', {});
	}
	return (await summarize(error));
};

function addError(error: TErrorExtended) {
	all.push(error);
	last = error;
	return error;
};

export const getFromName = (() => {
	const errors = Object.freeze(
		[
			Error,
			EvalError,
			RangeError,
			ReferenceError,
			SyntaxError,
			TypeError
		].reduce((a, c) => {
			a[c.name] = c;
			return (a);
		}, <Record<string, unknown>>{})
	);
	return ((name: string) => errors[name]);
})();

export function getLast() {
	return (last);
};

export function getAll() {
	return (all);
};

export function removeHandledError(error: TErrorExtended) {
	all.splice(all.indexOf(error), 1);
	if (last === error) {
		last = all[all.length - 1] || null;
	}
};

export function userAssert(condition: any, ...args: Parameters<typeof user>): asserts condition {
	if (!condition) {
		throw user(...args);
	}
};

export function libraryAssert(condition: any, ...args: Parameters<typeof library>): asserts condition {
	if (!condition) {
		throw library(...args);
	}
};

import * as O from './object';
